import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Autocomplete, Box, Button, Chip, FormControl, IconButton, InputLabel, MenuItem,
    Paper, Select, Stack, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import { raiseTicket, findSalesForTicket } from '../../api/supportApi';
import { CATEGORIES, PRIORITIES, PP } from './supportConstants';

const cardSx = { border: '1px solid #E2E8F0', borderRadius: '12px', p: { xs: 2, md: 2.5 }, bgcolor: '#fff' };
const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: PP } };

/* ============================================================================
   ASK FOR HELP — a page

   Opened from the tickets list, and from anywhere that wants to raise a ticket
   already pointing at something: /support/new?related=Sop&ref=step-5 fills in the
   category and remembers what it is about, so a gap in the SOP arrives attached
   to the step rather than as a sentence somebody has to decode.
   (user 2026-10-03: "create ticket aso can")
   ========================================================================= */
export default function SupportNewPage() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const relatedType = params.get('related') || null;
    const relatedRef = params.get('ref') || null;

    const [subject, setSubject] = useState(relatedType === 'Sop' ? 'SOP — ' : '');
    const [category, setCategory] = useState(relatedType === 'Sop' ? 'SOP' : 'Other');
    const [priority, setPriority] = useState('Normal');
    const [body, setBody] = useState('');
    // The sale this is about, if any. A ticket that names a customer can be answered
    // without asking "which one?" first. (user 2026-10-04)
    const [sale, setSale] = useState(null);
    const [saleQuery, setSaleQuery] = useState('');
    const [saleOptions, setSaleOptions] = useState([]);
    const [searching, setSearching] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    // Typed, not pressed: a short pause after typing is enough, and it keeps the list
    // from firing on every keystroke.
    useEffect(() => {
        const q = saleQuery.trim();
        if (sale || q.length < 2) { setSaleOptions([]); return undefined; }
        let gone = false;
        setSearching(true);
        const t = setTimeout(async () => {
            try {
                const res = await findSalesForTicket(q);
                if (!gone) setSaleOptions(res.data || []);
            } catch {
                if (!gone) setSaleOptions([]);
            } finally {
                if (!gone) setSearching(false);
            }
        }, 350);
        return () => { gone = true; clearTimeout(t); };
    }, [saleQuery, sale]);

    const submit = async () => {
        if (!subject.trim() || !body.trim()) {
            setError('A subject and a description, please.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const res = await raiseTicket({
                subject: subject.trim(),
                category,
                priority,
                body: body.trim(),
                // A chosen sale wins over whatever the URL carried - somebody who opened
                // this from the SOP and then named a customer means the customer.
                relatedType: sale ? 'Sales' : relatedType,
                relatedRef: sale ? String(sale.salesId) : relatedRef,
            });
            navigate(`/support/${res.data.supportTicketId}`);
        } catch (e) {
            setError(e?.response?.data?.message || 'Could not raise that ticket.');
            setBusy(false);
        }
    };

    return (
        <Box sx={{ width: '100%', fontFamily: PP, pb: 6 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <IconButton size="small" onClick={() => navigate('/support')}
                    sx={{ color: '#64748B', border: '1px solid #E2E8F0', borderRadius: '8px', p: 0.75 }}>
                    <ArrowBackIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Box>
                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, lineHeight: 1.2 }}>
                        Create ticket
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B', fontFamily: PP }}>
                        {relatedType === 'Sop'
                            ? 'About the SOP — say which step, and what is missing or wrong.'
                            : 'It goes to the people who can fix it, and you will get a reply here.'}
                    </Typography>
                </Box>
            </Stack>

            {error && (
                <Box sx={{ bgcolor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', p: 1.5, mb: 2 }}>
                    <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#DC2626' }}>{error}</Typography>
                </Box>
            )}

            <Paper elevation={0} sx={cardSx}>
                <Stack spacing={2.25}>
                    <TextField size="small" label="Subject" sx={fieldSx} value={subject}
                        onChange={e => setSubject(e.target.value)} inputProps={{ maxLength: 200 }}
                        placeholder="One line — what is wrong, or what you need" />

                    {/* Which customer this is about. Typing a name, a phone number or a
                        sales id finds the sale; choosing one means the person answering
                        sees the customer, the system size and the price without going to
                        look them up. Optional - plenty of questions are about nothing in
                        particular. (user 2026-10-04) */}
                    <Autocomplete
                        size="small"
                        options={saleOptions}
                        value={sale}
                        loading={searching}
                        onChange={(_, v) => setSale(v)}
                        onInputChange={(_, v, reason) => { if (reason === 'input') setSaleQuery(v); }}
                        isOptionEqualToValue={(a, b) => a.salesId === b.salesId}
                        getOptionLabel={(o) => o ? `${o.customerName} · #${o.salesId}` : ''}
                        filterOptions={(x) => x}      // the server already filtered
                        noOptionsText={saleQuery.trim().length < 2
                            ? 'Type a customer name, phone or sales id'
                            : 'No sale of yours matches that'}
                        renderOption={(props, o) => (
                            <li {...props} key={o.salesId}>
                                <Box sx={{ py: 0.25 }}>
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.86rem', fontWeight: 600 }}>
                                        {o.customerName}
                                    </Typography>
                                    <Typography sx={{ fontFamily: PP, fontSize: '0.74rem', color: '#64748B' }}>
                                        #{o.salesId}
                                        {o.city ? ` · ${o.city}` : ''}
                                        {o.energy ? ` · ${o.energy} kWp` : ''}
                                        {o.status ? ` · ${o.status}` : ''}
                                    </Typography>
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField {...params} sx={fieldSx} label="Customer / sale (optional)"
                                placeholder="Name, phone or sales id" />
                        )}
                    />

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControl size="small" fullWidth sx={fieldSx}>
                            <InputLabel sx={{ fontFamily: PP }}>Category</InputLabel>
                            <Select label="Category" value={category} onChange={e => setCategory(e.target.value)}
                                sx={{ fontFamily: PP }}>
                                {CATEGORIES.map(c => <MenuItem key={c} value={c} sx={{ fontFamily: PP }}>{c}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth sx={fieldSx}>
                            <InputLabel sx={{ fontFamily: PP }}>Priority</InputLabel>
                            <Select label="Priority" value={priority} onChange={e => setPriority(e.target.value)}
                                sx={{ fontFamily: PP }}>
                                {PRIORITIES.map(p => <MenuItem key={p} value={p} sx={{ fontFamily: PP }}>{p}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Stack>

                    <TextField size="small" label="What do you need?" multiline minRows={7} sx={fieldSx}
                        value={body} onChange={e => setBody(e.target.value)} inputProps={{ maxLength: 4000 }}
                        placeholder="What were you doing, what happened, and what did you expect instead?" />

                    {relatedRef && (
                        <Typography sx={{ fontFamily: PP, fontSize: '0.76rem', color: '#64748B' }}>
                            This ticket will be tagged to {relatedType} {relatedRef}.
                        </Typography>
                    )}
                </Stack>
            </Paper>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                <Button variant="outlined" onClick={() => navigate('/support')}
                    sx={{ fontFamily: PP, textTransform: 'none', borderRadius: '8px' }}>
                    Cancel
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" startIcon={<SendIcon />} onClick={submit} disabled={busy}
                    sx={{ fontFamily: PP, textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
                    {busy ? 'Sending…' : 'Send'}
                </Button>
            </Stack>
        </Box>
    );
}
